import React, { useState, useEffect } from "react";
import styles from "./AddProduct.module.css";
import { Product } from "../../../interface/product";
import { getAllSeries } from "../../../service/series";
import { createProduct, updateProduct } from "../../../service/product";
import { uploadMultipleAttachments } from "../../../service/file-service";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
interface Attribute {
  key: string;
  value: string;
}

interface AddProductProps {
  onSuccess: () => void;
  initialData?: Product | null;
}

const AddProduct: React.FC<AddProductProps> = ({ onSuccess, initialData }) => {
  // Quill modules configuration
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
      ["clean"],
    ],
  };

  const [series, setSeries] = useState<any[]>([]);
  const [isLoadingSeries, setIsLoadingSeries] = useState(true);

  const [formData, setFormData] = useState({
    productId: "",
    name: "",
    color: "",
    price: "",
    ourPrice: "",
    seriesId: "",
    description: "",
  });

  const [attributes, setAttributes] = useState<Attribute[]>([
    { key: "", value: "" },
  ]);

  // const [selectedImage, setSelectedImage] = useState<File | null>(null);
  // const [currentImage, setCurrentImage] = useState<string | null>(null);
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  // Changed from single image URL to array of URLs/objects
  const [currentImages, setCurrentImages] = useState<
    Array<string | { path: string }>
  >([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchSeries = async () => {
      try {
        const seriesData = await getAllSeries();
        setSeries(seriesData);

        // Set initial series ID if provided
        if (initialData && seriesData.length > 0) {
          // Find the series that matches the product's series
          const series = seriesData.find(
            (s: any) => s.id === initialData.series
          );
          if (series) {
            setFormData((prev) => ({ ...prev, seriesId: series.id }));
          }
        }
      } catch (err) {
        console.error("Failed to fetch series:", err);
        setError("Failed to load series data");
      } finally {
        setIsLoadingSeries(false);
      }
    };

    fetchSeries();
  }, [initialData]);

  useEffect(() => {
    if (initialData) {
      // Pre-populate form with initial data
      setFormData({
        productId: initialData.productId,
        name: initialData.name,
        color: initialData.color || "",
        price: initialData.price.toString(),
        ourPrice: initialData.ourPrice.toString(),
        seriesId: "",
        description: initialData.description || "",
      });

      if (
        // @ts-ignore
        initialData?.pictures &&
        // @ts-ignore

        initialData?.pictures.length > 0
      ) {
        // @ts-ignore

        setCurrentImages(initialData.pictures);
      } else if (initialData.picture) {
        // Backward compatibility with single image
        setCurrentImages([initialData.picture]);
      }
      // Convert attributes to array
      if (initialData.attributes) {
        const attrArray = Object.entries(initialData.attributes).map(
          ([key, value]) => ({ key, value: value.toString() })
        );
        setAttributes(
          attrArray.length > 0 ? attrArray : [{ key: "", value: "" }]
        );
      }
    }
  }, [initialData]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAttributeChange = (
    index: number,
    field: keyof Attribute,
    value: string
  ) => {
    setAttributes((prev) => {
      const newAttrs = [...prev];
      newAttrs[index] = { ...newAttrs[index], [field]: value };
      return newAttrs;
    });
  };

  const addAttribute = () => {
    setAttributes((prev) => [...prev, { key: "", value: "" }]);
  };

  const removeAttribute = (index: number) => {
    if (attributes.length > 1) {
      setAttributes((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      setSelectedImages((prev) => [...prev, ...newFiles]);
      // Reset the file input to allow selecting the same files again
      e.target.value = "";
    }
  };

  const removeImage = (index: number, isCurrentImage: boolean) => {
    if (isCurrentImage) {
      setCurrentImages((prev) => prev.filter((_, i) => i !== index));
    } else {
      setSelectedImages((prev) => prev.filter((_, i) => i !== index));
    }
  };
  const handleDescriptionChange = (value: string) => {
    setFormData((prev) => ({ ...prev, description: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage("");
    setError("");

    try {
      // Validate required fields
      if (!formData.productId || !formData.name || !formData.seriesId) {
        throw new Error("Product ID, Name, and Series are required");
      }

      // Validate prices
      const priceNum = parseFloat(formData.price);
      const ourPriceNum = parseFloat(formData.ourPrice);

      if (isNaN(priceNum) || isNaN(ourPriceNum)) {
        throw new Error("Prices must be valid numbers");
      }

      if (ourPriceNum > priceNum) {
        throw new Error(
          "Our price must be less than or equal to regular price"
        );
      }

      // Prepare attributes object
      const attributesObj: Record<string, any> = {};
      attributes.forEach((attr) => {
        if (attr.key && attr.value) {
          attributesObj[attr.key] = attr.value;
        }
      });

      // Upload all selected images
      let uploadedImages;

      // First upload new images

      if (selectedImages.length > 0) {
        const filesResponse = await uploadMultipleAttachments(selectedImages);
        uploadedImages = filesResponse?.files;
      }

      const productData = {
        ...formData,
        price: priceNum,
        ourPrice: ourPriceNum,
        attributes: attributesObj,
        pictures: uploadedImages,
      };

      // Prepare product data
      if (initialData) {
        // Update existing product
        await updateProduct(initialData._id, productData);
        setMessage("Product updated successfully!");
      } else {
        // Create new product
        await createProduct(productData);
        setMessage("Product created successfully!");

        // Reset form for new entries
        setFormData({
          productId: "",
          name: "",
          color: "",
          price: "",
          ourPrice: "",
          seriesId: "",
          description: "",
        });
        setAttributes([{ key: "", value: "" }]);
        setSelectedImages([]);
        setSelectedImages([]);
        setCurrentImages([]);
      }

      onSuccess();
    } catch (err: any) {
      console.error("Error saving product:", err);
      setError(err.message || "Failed to save product");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>
        {initialData ? "Update Product" : "Add New Product"}
      </h2>

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.grid}>
          <div className={styles.formGroup}>
            <label className={styles.label}>Product ID *</label>
            <input
              type="text"
              name="productId"
              value={formData.productId}
              onChange={handleChange}
              required
              className={styles.input}
              placeholder="e.g., K-601"
              disabled={!!initialData}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Series *</label>
            <select
              name="seriesId"
              value={formData.seriesId}
              onChange={handleChange}
              required
              className={styles.input}
              disabled={isLoadingSeries || !!initialData}
            >
              <option value="">Select a series</option>
              {series.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.title}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Product Name *</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className={styles.input}
            placeholder="e.g., Steel Bar Stool Chair (Black)"
          />
        </div>

        <div className={styles.grid}>
          <div className={styles.formGroup}>
            <label className={styles.label}>Price (₹) *</label>
            <input
              type="number"
              name="price"
              value={formData.price}
              onChange={handleChange}
              required
              min="0"
              step="0.01"
              className={styles.input}
              placeholder="e.g., 129.00"
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Our Price (₹) *</label>
            <input
              type="number"
              name="ourPrice"
              value={formData.ourPrice}
              onChange={handleChange}
              required
              min="0"
              step="0.01"
              className={styles.input}
              placeholder="e.g., 109.00"
            />
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Color</label>
          <input
            type="text"
            name="color"
            value={formData.color}
            onChange={handleChange}
            className={styles.input}
            placeholder="e.g., Black"
          />
        </div>

        {/* <div className={styles.formGroup}>
          <label className={styles.label}>Description</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={3}
            className={`${styles.input} ${styles.textarea}`}
            placeholder="Describe the product..."
          />
        </div> */}
        <div className={styles.formGroup}>
          <label className={styles.label}>Description</label>
          <ReactQuill
            value={formData.description}
            onChange={handleDescriptionChange}
            modules={quillModules}
            className={styles.quillEditor}
          />
        </div>
        <div className={styles.formGroup}>
          <div className={styles.attributeHeader}>
            <label className={styles.label}>Attributes</label>
            <button
              type="button"
              onClick={addAttribute}
              className={styles.addButton}
            >
              + Add Attribute
            </button>
          </div>

          {attributes.map((attr, index) => (
            <div key={index} className={styles.attributeRow}>
              <input
                type="text"
                value={attr.key}
                onChange={(e) =>
                  handleAttributeChange(index, "key", e.target.value)
                }
                className={styles.input}
                placeholder="Attribute (e.g., adjustable)"
              />
              <input
                type="text"
                value={attr.value}
                onChange={(e) =>
                  handleAttributeChange(index, "value", e.target.value)
                }
                className={styles.input}
                placeholder="Value (e.g., Yes)"
              />
              <button
                type="button"
                onClick={() => removeAttribute(index)}
                className={styles.removeButton}
                disabled={attributes.length <= 1}
              >
                Remove
              </button>
            </div>
          ))}
        </div>

        {/* Image Upload Section */}
        <div className={styles.formGroup}>
          <label className={styles.label}>Product Images</label>

          {/* Display current images */}
          {currentImages.length > 0 && (
            <div className={styles.imageGrid}>
              {currentImages.map((img, index) => (
                <div
                  key={`current-${index}`}
                  className={styles.imagePreviewContainer}
                >
                  <img
                    src={typeof img === "string" ? img : img.path}
                    alt={`Product ${index + 1}`}
                    className={styles.imagePreview}
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(index, true)}
                    className={styles.removeImageButton}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Display selected new images */}
          {selectedImages.length > 0 && (
            <div className={styles.imageGrid}>
              {selectedImages.map((file, index) => (
                <div
                  key={`new-${index}`}
                  className={styles.imagePreviewContainer}
                >
                  <img
                    src={URL.createObjectURL(file)}
                    alt={`New image ${index + 1}`}
                    className={styles.imagePreview}
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(index, false)}
                    className={styles.removeImageButton}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* File upload input */}
          <div className={styles.fileUpload}>
            <label className={styles.fileUploadLabel}>
              Choose images...
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className={styles.fileInput}
                multiple
              />
            </label>
          </div>
          <p className={styles.hint}>You can select multiple images at once</p>
        </div>
        <button
          type="submit"
          className={styles.submitButton}
          disabled={isSubmitting}
        >
          {isSubmitting
            ? initialData
              ? "Updating..."
              : "Creating..."
            : initialData
            ? "Update Product"
            : "Create Product"}
        </button>

        {message && <div className={styles.successMessage}>{message}</div>}
        {error && <div className={styles.errorMessage}>{error}</div>}
      </form>
    </div>
  );
};

export default AddProduct;
